import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import {
  KeyRound, Webhook, Plus, Copy, RefreshCw, Ban, Trash2, Send, CheckCircle2, XCircle, ShieldCheck, History, RotateCw,
} from 'lucide-react';

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

export const IntegrationPanel = () => {
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState('');
  const [reveal, setReveal] = useState(null); // { title, value, note }
  const [newHook, setNewHook] = useState({ url: '', events: [] });
  const [showHookForm, setShowHookForm] = useState(false);
  const [historyHook, setHistoryHook] = useState(null); // webhook object
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [k, h, e] = await Promise.all([
        api.get('/admin/integration/api-keys'),
        api.get('/admin/integration/webhooks'),
        api.get('/admin/integration/events'),
      ]);
      setKeys(k.data.data);
      setHooks(h.data.data);
      setEvents(e.data.data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async (val) => {
    try { await navigator.clipboard.writeText(val); toast.success('Copied to clipboard'); }
    catch { toast.error('Could not copy — select and copy manually'); }
  };

  // ---------- API KEYS ----------
  const generateKey = async () => {
    try {
      const r = await api.post('/admin/integration/api-keys', { name: newKeyName || 'Dashboard key' });
      setNewKeyName('');
      setReveal({
        title: 'API key created', value: r.data.data.raw_key,
        note: 'Copy this key now. For security it is shown only once and is never stored in readable form. Give it to the Dashboard server only — never put it in browser code.',
      });
      await load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this key? The Dashboard using it will stop syncing immediately.')) return;
    try { await api.post(`/admin/integration/api-keys/${id}/revoke`); toast.success('Key revoked'); await load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const replaceKey = async (id) => {
    if (!window.confirm('Replace this key? A new key is issued and the old one stops working.')) return;
    try {
      const r = await api.post(`/admin/integration/api-keys/${id}/rotate`);
      setReveal({
        title: 'Key replaced', value: r.data.data.raw_key,
        note: 'The old key is now revoked. Copy the new key now — it is shown only once.',
      });
      await load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const deleteKey = async (id) => {
    if (!window.confirm('Permanently delete this key record?')) return;
    try { await api.delete(`/admin/integration/api-keys/${id}`); toast.success('Key deleted'); await load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  // ---------- WEBHOOKS ----------
  const toggleHookEvent = (ev) => setNewHook((h) => ({
    ...h, events: h.events.includes(ev) ? h.events.filter((x) => x !== ev) : [...h.events, ev],
  }));
  const createHook = async () => {
    if (!newHook.url.trim()) { toast.error('Enter the Dashboard webhook URL.'); return; }
    try {
      const r = await api.post('/admin/integration/webhooks', {
        url: newHook.url.trim(), events: newHook.events.length ? newHook.events : undefined,
      });
      setReveal({
        title: 'Webhook created — signing secret', value: r.data.data.secret,
        note: 'Store this signing secret on your Dashboard to verify the X-Shabz-Signature header. It is shown only once.',
      });
      setNewHook({ url: '', events: [] });
      setShowHookForm(false);
      await load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const toggleHookActive = async (h) => {
    try { await api.patch(`/admin/integration/webhooks/${h.id}`, { active: !h.active }); await load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const testHook = async (id) => {
    try { await api.post(`/admin/integration/webhooks/${id}/test`); toast.success('Test event queued'); setTimeout(load, 1500); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const rotateSecret = async (h) => {
    if (!window.confirm('Issue a NEW signing secret for this webhook? The URL and events stay the same. You must update BILLING_INTEGRATION_WEBHOOK_SECRET on the Dashboard with the new value.')) return;
    try {
      const r = await api.post(`/admin/integration/webhooks/${h.id}/rotate-secret`);
      setReveal({
        title: 'New webhook signing secret',
        value: r.data.data.secret,
        note: 'Copy this now and set it as BILLING_INTEGRATION_WEBHOOK_SECRET on your Dashboard. It is shown only once. The webhook URL and events are unchanged.',
      });
      await load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const deleteHook = async (id) => {
    if (!window.confirm('Delete this webhook?')) return;
    try { await api.delete(`/admin/integration/webhooks/${id}`); toast.success('Webhook deleted'); await load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  // ---------- DELIVERY HISTORY ----------
  const openHistory = async (hook) => {
    setHistoryHook(hook);
    setLoadingDeliveries(true);
    setDeliveries([]);
    try {
      const r = await api.get(`/admin/integration/webhooks/${hook.id}/deliveries`, { params: { limit: 50 } });
      setDeliveries(r.data.data);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoadingDeliveries(false); }
  };
  const refreshHistory = async () => { if (historyHook) await openHistory(historyHook); };
  const resendDelivery = async (deliveryId) => {
    setResendingId(deliveryId);
    try {
      await api.post(`/admin/integration/deliveries/${deliveryId}/resend`);
      toast.success('Re-queued — same event id is re-sent (no duplicate created)');
      setTimeout(refreshHistory, 1500);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setResendingId(null); }
  };
  const deliveryTone = (s) => (
    s === 'delivered' ? 'bg-primary/10 text-primary'
      : s === 'failed' ? 'bg-destructive/10 text-destructive'
      : s === 'cancelled' ? 'bg-muted text-muted-foreground'
      : 'bg-amber-500/10 text-amber-600');

  const StatusBadge = ({ active, revoked }) => (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
      revoked ? 'bg-destructive/10 text-destructive'
        : active ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
      {revoked ? 'Revoked' : active ? 'Active' : 'Inactive'}
    </span>
  );

  return (
    <>
      {/* API KEYS */}
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="integration-keys-card">
        <div className="mb-1 flex items-center gap-2"><KeyRound className="h-4 w-4" /><p className="text-sm font-semibold">Dashboard integration — API keys</p></div>
        <p className="mb-3 text-xs text-muted-foreground">Server-to-server, read-only keys for your external Business Dashboard. The full key is shown once at creation and never stored in readable form. Never put a key in browser/website code.</p>

        <div className="mb-3 flex gap-2">
          <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder="Key name (e.g. Dashboard Prod)" className="h-11 rounded-xl" data-testid="integration-newkey-name" />
          <Button className="h-11 rounded-xl" onClick={generateKey} data-testid="integration-generate-key"><Plus className="mr-1 h-4 w-4" />Generate</Button>
        </div>

        <div className="space-y-2">
          {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
          {!loading && keys.length === 0 && <p className="text-xs text-muted-foreground">No API keys yet.</p>}
          {keys.map((k) => (
            <div key={k.id} className="rounded-xl border border-border p-3" data-testid={`integration-key-${k.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{k.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{k.prefix}…</p>
                </div>
                <StatusBadge active={k.active} revoked={!!k.revoked_at} />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                <span>Created: {fmtDate(k.created_at)}</span>
                <span>Last used: {fmtDate(k.last_used_at)}</span>
                <span>Requests: {k.usage_count || 0}</span>
                {k.revoked_at && <span>Revoked: {fmtDate(k.revoked_at)}</span>}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {k.active && !k.revoked_at && (
                  <button onClick={() => revokeKey(k.id)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs" data-testid={`integration-key-revoke-${k.id}`}><Ban className="h-3 w-3" />Revoke</button>
                )}
                <button onClick={() => replaceKey(k.id)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs" data-testid={`integration-key-replace-${k.id}`}><RefreshCw className="h-3 w-3" />Replace</button>
                <button onClick={() => deleteKey(k.id)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs text-destructive" data-testid={`integration-key-delete-${k.id}`}><Trash2 className="h-3 w-3" />Delete</button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-start gap-2 rounded-xl border border-border bg-muted/40 p-2.5 text-[11px] text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>Use header <b>X-API-Key</b> against <b>/api/integration/v1/…</b>. First connection: full paginated backfill; afterwards pass <b>updated_since</b> for incremental sync.</p>
        </div>
      </div>

      {/* WEBHOOKS */}
      <div className="rounded-2xl border border-border bg-card p-4" data-testid="integration-webhooks-card">
        <div className="mb-1 flex items-center gap-2"><Webhook className="h-4 w-4" /><p className="text-sm font-semibold">Dashboard integration — webhooks</p></div>
        <p className="mb-3 text-xs text-muted-foreground">Push signed events to your Dashboard in real time. Each request carries an HMAC <b>X-Shabz-Signature</b>. Delivery is retried automatically and never blocks a sale or stock change.</p>

        {!showHookForm && (
          <Button variant="secondary" className="mb-3 h-11 w-full rounded-xl" onClick={() => setShowHookForm(true)} data-testid="integration-add-webhook-btn"><Plus className="mr-1 h-4 w-4" />Add webhook</Button>
        )}
        {showHookForm && (
          <div className="mb-3 rounded-xl border border-border p-3" data-testid="integration-webhook-form">
            <Label className="text-xs">Dashboard endpoint URL</Label>
            <Input value={newHook.url} onChange={(e) => setNewHook({ ...newHook, url: e.target.value })} placeholder="https://your-dashboard.com/webhooks/shabz" className="mt-1 h-11 rounded-xl" data-testid="integration-webhook-url" />
            <p className="mb-2 mt-3 text-xs font-medium">Events <span className="font-normal text-muted-foreground">(none = all)</span></p>
            <div className="flex flex-wrap gap-2">
              {events.map((ev) => (
                <button key={ev} onClick={() => toggleHookEvent(ev)} data-testid={`integration-event-${ev}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium ${newHook.events.includes(ev) ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card'}`}>{ev}</button>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button variant="secondary" className="h-11 flex-1 rounded-xl" onClick={() => { setShowHookForm(false); setNewHook({ url: '', events: [] }); }}>Cancel</Button>
              <Button className="h-11 flex-1 rounded-xl" onClick={createHook} data-testid="integration-webhook-save">Create webhook</Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {!loading && hooks.length === 0 && <p className="text-xs text-muted-foreground">No webhooks configured.</p>}
          {hooks.map((h) => (
            <div key={h.id} className="rounded-xl border border-border p-3" data-testid={`integration-webhook-${h.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="min-w-0 truncate text-sm font-medium">{h.url}</p>
                <StatusBadge active={h.active} />
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(h.events || []).map((ev) => <span key={ev} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">{ev}</span>)}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-primary" />{fmtDate(h.last_success_at)}</span>
                <span className="inline-flex items-center gap-1"><XCircle className="h-3 w-3 text-destructive" />{fmtDate(h.last_failure_at)}</span>
                <span>Secret: <span className="font-mono">{h.secret_hint || '—'}</span></span>
              </div>
              {h.last_error && <p className="mt-1 truncate text-[11px] text-destructive">Last error: {h.last_error}</p>}
              <div className="mt-2 flex flex-wrap gap-2">
                <button onClick={() => toggleHookActive(h)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs" data-testid={`integration-webhook-toggle-${h.id}`}>{h.active ? 'Pause' : 'Resume'}</button>
                <button onClick={() => testHook(h.id)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs" data-testid={`integration-webhook-test-${h.id}`}><Send className="h-3 w-3" />Test</button>
                <button onClick={() => rotateSecret(h)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs" data-testid={`integration-webhook-rotate-${h.id}`}><KeyRound className="h-3 w-3" />Rotate secret</button>
                <button onClick={() => openHistory(h)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs" data-testid={`integration-webhook-history-${h.id}`}><History className="h-3 w-3" />History</button>
                <button onClick={() => deleteHook(h.id)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs text-destructive" data-testid={`integration-webhook-delete-${h.id}`}><Trash2 className="h-3 w-3" />Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* DELIVERY HISTORY DIALOG */}
      <Dialog open={!!historyHook} onOpenChange={(o) => { if (!o) { setHistoryHook(null); setDeliveries([]); } }}>
        <DialogContent className="max-h-[85vh] overflow-hidden" data-testid="integration-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" />Delivery history</DialogTitle>
            <DialogDescription className="truncate">{historyHook?.url}</DialogDescription>
          </DialogHeader>
          <div className="mb-1 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Most recent {deliveries.length} events</p>
            <Button size="sm" variant="secondary" className="h-8 rounded-lg" onClick={refreshHistory} data-testid="integration-history-refresh"><RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh</Button>
          </div>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
            {loadingDeliveries && <p className="text-xs text-muted-foreground">Loading…</p>}
            {!loadingDeliveries && deliveries.length === 0 && <p className="text-xs text-muted-foreground">No deliveries yet. Trigger an event or use “Test”.</p>}
            {deliveries.map((d) => (
              <div key={d.delivery_id} className="rounded-xl border border-border p-3" data-testid={`integration-delivery-${d.delivery_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-semibold">{d.event}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${deliveryTone(d.status)}`}>{d.status}</span>
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span className="col-span-2 truncate">Event ID: <span className="font-mono">{d.delivery_id}</span></span>
                  <span>Attempts: {d.attempts}</span>
                  <span>HTTP: {d.response_code ?? '—'}</span>
                  <span>Created: {fmtDate(d.created_at)}</span>
                  <span>Delivered: {fmtDate(d.delivered_at)}</span>
                </div>
                {d.last_error && <p className="mt-1 truncate text-[11px] text-destructive">Error: {d.last_error}</p>}
                {(d.attempts_log || []).length > 0 && (
                  <div className="mt-2 space-y-1 rounded-lg bg-muted/40 p-2">
                    {d.attempts_log.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 text-[10px]">
                        {a.ok ? <CheckCircle2 className="h-3 w-3 text-primary" /> : <XCircle className="h-3 w-3 text-destructive" />}
                        <span className="font-medium">#{a.attempt}</span>
                        <span className="text-muted-foreground">{fmtDate(a.ts)}</span>
                        <span className="ml-auto">HTTP {a.response_code ?? '—'}</span>
                        {a.error && <span className="max-w-[40%] truncate text-destructive">{a.error}</span>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2">
                  <button onClick={() => resendDelivery(d.delivery_id)} disabled={resendingId === d.delivery_id}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs disabled:opacity-50" data-testid={`integration-delivery-resend-${d.delivery_id}`}>
                    <RotateCw className={`h-3 w-3 ${resendingId === d.delivery_id ? 'animate-spin' : ''}`} />Resend
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-1 flex items-start gap-2 rounded-xl border border-border bg-muted/40 p-2.5 text-[11px] text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>Resend re-sends the <b>same event id</b>, so a correct receiver deduplicates it — no duplicate sale, purchase or expense is ever created.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* REVEAL-ONCE DIALOG */}
      <Dialog open={!!reveal} onOpenChange={(o) => { if (!o) setReveal(null); }}>
        <DialogContent data-testid="integration-reveal-dialog">
          <DialogHeader>
            <DialogTitle>{reveal?.title}</DialogTitle>
            <DialogDescription>{reveal?.note}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 rounded-xl border border-border bg-muted/50 p-3">
            <code className="min-w-0 flex-1 break-all font-mono text-xs" data-testid="integration-reveal-value">{reveal?.value}</code>
            <Button size="sm" className="shrink-0 rounded-lg" onClick={() => copy(reveal?.value)} data-testid="integration-reveal-copy"><Copy className="mr-1 h-3.5 w-3.5" />Copy</Button>
          </div>
          <DialogFooter>
            <Button className="w-full rounded-xl" onClick={() => setReveal(null)} data-testid="integration-reveal-done">I've saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
